import { ErrorHandler, Injectable, Injector, NgZone, InjectionToken } from '@angular/core';

/**
 * Injection token for LoggingService to avoid circular dependencies
 */
export const LOGGING_SERVICE = new InjectionToken<any>('LoggingService');

/**
 * GlobalErrorHandler captures unhandled Angular exceptions and integrates
 * them into the logging system. Extends Angular's ErrorHandler
 * to provide structured error logging with debouncing and zone-aware execution.
 * 
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  // Error debouncing to prevent rapid-fire duplicate errors
  private lastErrorMessage: string | null = null;
  private lastErrorTime: number = 0;
  private readonly DEBOUNCE_WINDOW_MS = 1000; // 1 second

  // Lazy injection to avoid circular dependencies
  private injector: Injector;
  private ngZone: NgZone;
  private loggingService: any = null;

  // Original console.error for fallback
  private originalConsoleError: (...args: any[]) => void;

  constructor(injector: Injector, ngZone: NgZone) {
    this.injector = injector;
    this.ngZone = ngZone;
    
    // Store original console.error for fallback
    this.originalConsoleError = console.error.bind(console);
  }

  /**
   * Handle unhandled errors with full stack trace capture
   */
  handleError(error: any): void {
    // Execute outside Angular zone to prevent change detection loops
    this.ngZone.runOutsideAngular(() => {
      try {
        // Extract error message and stack trace
        const errorMessage = this.extractErrorMessage(error);
        const errorStack = this.extractStackTrace(error);
        
        // Debounce duplicate errors within 1-second window
        if (this.isDuplicateError(errorMessage)) {
          return;
        }

        // Update debouncing state
        this.lastErrorMessage = errorMessage;
        this.lastErrorTime = Date.now();

        // Extract error metadata
        const errorMetadata = this.extractErrorMetadata(error);

        // Log error using LoggingService if available
        this.logError(errorMessage, {
          stack: errorStack,
          ...errorMetadata
        });

      } catch (handlingError) {
        // Fallback to original console.error if error handling fails
        this.originalConsoleError('Error in GlobalErrorHandler:', handlingError);
        this.originalConsoleError('Original error:', error);
      }
    });
  }

  /**
   * Extract error message from various error types
   */
  private extractErrorMessage(error: any): string {
    if (!error) {
      return 'Unknown error';
    }

    // Handle Error objects
    if (error instanceof Error) {
      return error.message || error.toString();
    }

    // Handle error events
    if (error.message) {
      return error.message;
    }

    // Handle rejection events
    if (error.rejection) {
      return this.extractErrorMessage(error.rejection);
    }

    // Handle string errors
    if (typeof error === 'string') {
      return error;
    }

    // Fallback to string representation
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }

  /**
   * Extract full stack trace from error
   */
  private extractStackTrace(error: any): string | undefined {
    if (!error) {
      return undefined;
    }

    // Handle Error objects with stack property
    if (error instanceof Error && error.stack) {
      return error.stack;
    }

    // Handle error events with stack
    if (error.stack) {
      return error.stack;
    }

    // Handle rejection events
    if (error.rejection && error.rejection.stack) {
      return error.rejection.stack;
    }

    return undefined;
  }

  /**
   * Extract additional error metadata for classification
   */
  private extractErrorMetadata(error: any): any {
    const metadata: any = {
      errorType: this.classifyError(error),
      timestamp: new Date().toISOString()
    };

    // Add error name if available
    if (error instanceof Error && error.name) {
      metadata.errorName = error.name;
    }

    // Add error code if available
    if (error.code) {
      metadata.errorCode = error.code;
    }

    // Add HTTP status if available (for HTTP errors)
    if (error.status) {
      metadata.httpStatus = error.status;
    }

    // Add URL if available (for HTTP errors)
    if (error.url) {
      metadata.url = error.url;
    }

    // Add rejection reason if available (for promise rejections)
    if (error.rejection) {
      metadata.rejectionReason = this.extractErrorMessage(error.rejection);
    }

    return metadata;
  }

  /**
   * Classify error type for better categorization
   */
  private classifyError(error: any): string {
    if (!error) {
      return 'UNKNOWN';
    }

    // HTTP errors
    if (error.status !== undefined) {
      return 'HTTP_ERROR';
    }

    // Promise rejections
    if (error.rejection !== undefined) {
      return 'PROMISE_REJECTION';
    }

    // Type errors
    if (error instanceof TypeError) {
      return 'TYPE_ERROR';
    }

    // Reference errors
    if (error instanceof ReferenceError) {
      return 'REFERENCE_ERROR';
    }

    // Syntax errors
    if (error instanceof SyntaxError) {
      return 'SYNTAX_ERROR';
    }

    // Range errors
    if (error instanceof RangeError) {
      return 'RANGE_ERROR';
    }

    // Generic Error
    if (error instanceof Error) {
      return 'ERROR';
    }

    return 'UNKNOWN';
  }

  /**
   * Check if error is a duplicate within debounce window
   */
  private isDuplicateError(errorMessage: string): boolean {
    const now = Date.now();
    const timeSinceLastError = now - this.lastErrorTime;

    // Check if same error message within 1-second window
    return (
      this.lastErrorMessage === errorMessage &&
      timeSinceLastError < this.DEBOUNCE_WINDOW_MS
    );
  }

  /**
   * Log error using LoggingService with lazy injection
   */
  private logError(message: string, data?: any): void {
    try {
      // Lazy inject LoggingService to avoid circular dependencies
      if (!this.loggingService) {
        try {
          // Use optional injection to avoid deprecation warnings
          this.loggingService = this.injector.get(LOGGING_SERVICE, null);
        } catch (error) {
          // LoggingService not available
          this.loggingService = null;
        }
      }

      // Use LoggingService if available
      if (this.loggingService && typeof this.loggingService.error === 'function') {
        this.loggingService.error(message, data);
      } else {
        // Fallback to original console.error when LoggingService unavailable
        this.fallbackToConsole(message, data);
      }

    } catch (error) {
      // Fallback to original console.error on any error
      this.fallbackToConsole(message, data);
    }
  }

  /**
   * Fallback to original console.error when LoggingService is unavailable
   */
  private fallbackToConsole(message: string, data?: any): void {
    if (data) {
      this.originalConsoleError('[ERROR]', message, data);
    } else {
      this.originalConsoleError('[ERROR]', message);
    }
  }
}
